import { useState } from 'react';

import { showModal } from 'modal.react.js';
import { useNetworkState } from 'react-use';
import { useQueryParam } from 'use-query-params';

import Share from 'components/Pages/Admin/Itineraries/Share';
import { type useLandingContext } from 'components/Pages/Admin/shared/ItinerariesItemLanding/Builder/functions';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { ACCOUNT_TYPE_AGENT, ITINERARIES_ITEM_TYPES } from 'constants/index';
import { showSnackbar } from 'hooks/useSnackbar';
import i18n from 'i18n';
import axiosApi from 'utils/axios';
import { copyTextToClipboard } from 'utils/common';
import { saveAsPDF } from 'utils/filesDownloaders/pdf';
import { ITINERARIES_ITEM_TYPE_TO_ROUTE_SEGMENT_MAP } from 'utils/itineraries/functions';

import s from './index.module.css';

export function useUpgradedItineraryContext(context: ReturnType<typeof useLandingContext>) {
  const [showAgentContactsModal, setShowAgentContactsModal] = useState(false);
  const [showShareWithFriends, setShareWithFriends] = useState(false);
  const { online } = useNetworkState();
  const [typeQuery] = useQueryParam('type');
  const [roleQuery] = useQueryParam('role');

  const { itinerariesItemType, page, headerOptions } = context;

  const isSampleItinerary = typeQuery === ITINERARIES_ITEM_TYPES.SAMPLE;
  const isAgentPage = page?.account?.type === ACCOUNT_TYPE_AGENT;
  const isUrlTypeAgent = roleQuery === ACCOUNT_TYPE_AGENT;
  const showBlock = isSampleItinerary ? isUrlTypeAgent : true;

  const phoneIcon = (context?.headerOptions?.icons || []).find((i) => i.type === 'phone');
  const printIcon = (context?.headerOptions?.icons || []).find((i) => i.type === 'print');
  const shareIcon = (context?.headerOptions?.icons || []).find((i) => i.type === 'share');
  const inviteIcon = (context?.headerOptions?.icons || []).find((i) => i.type === 'invite');

  if (phoneIcon) {
    phoneIcon.onClick = () => {
      setShowAgentContactsModal(true);
    };
  }

  if (printIcon) {
    printIcon.onClick = () => {
      // @ts-ignore
      saveAsPDF({ item: page, itinerariesItemType, translate: true });
    };
  }

  if (shareIcon) {
    shareIcon.onClick = () => {
      if (online) {
        axiosApi.post(`/${ITINERARIES_ITEM_TYPE_TO_ROUTE_SEGMENT_MAP[itinerariesItemType]}/${page.id}/copy_link`);
      }

      copyTextToClipboard(window.location.href);
      showSnackbar({ text: i18n.t('proposal_content.link_copied'), uniqueId: 'link-copied' });
    };
  }

  if (inviteIcon) {
    inviteIcon.onClick = () => {
      showModal(area, (close) => {
        return (
          <Modal contentClassName={s.shareModal} isOpen toClose={close} withoutCloseBtn>
            <Share
              builder={false}
              itemId={
                context.itinerariesItemType === ITINERARIES_ITEM_TYPES.ITINERARY
                  ? context.page.guestPortalId
                  : context.page.id
              }
              landingShareUrl={page.landingShareUrl}
              itinerariesItemType={ITINERARIES_ITEM_TYPES.GUEST_PORTAL}
              closeModal={close}
              showPermissions={context.manageHideCostsPermission}
              manageHideCosts={context.manageHideCostsPermission}
              hideHideCosts={context.page.itineraryCostsPageHidden}
            />
          </Modal>
        );
      });
    };
  }

  const closeAgentContactsModal = () => setShowAgentContactsModal(false);
  const closeShareWithFriendsModal = () => setShareWithFriends(false);

  const headerIcons = isSampleItinerary && isAgentPage ? [phoneIcon] : [...headerOptions.icons];

  return {
    context: { ...context, hasHeader: showBlock, headerOptions: { ...headerOptions, icons: headerIcons } },
    showAgentContactsModal,
    closeAgentContactsModal,
    showShareWithFriends,
    closeShareWithFriendsModal,
  };
}
